/**
 * Loudness stepping for the hearing test (shared with rht-web-app / mobile `timerServiceUtils`).
 * After the first hit: −10 dB on hit; on miss, +5 dB, or min(X+k, MAX) only when strictly > current+5,
 * where k is {@link MISS_JUMP_OVER_LOUDEST_MISS_DB} and X is the loudest missed intensity used as reference.
 * @see rht-web-app `loudestMissDbForMissJumpReference` for how X is chosen.
 */

export const MIN_DB = 0;
export const MAX_DB = 85;

/** dB above loudest qualifying missed level (X) when re-testing after consecutive responses following misses. */
export const MISS_JUMP_OVER_LOUDEST_MISS_DB = 15;

export type LoudnessTrialRow = {
  loudness: number;
  didUserRespond: boolean;
};

/**
 * Loudest stimulus level (dB) where the listener missed, among trials that occur after at least
 * one hit earlier in chronological order.
 */
export function loudestMissDbAfterPriorHit(
  trials: ReadonlyArray<LoudnessTrialRow>,
): number | null {
  let seenHit = false;
  let best: number | null = null;
  for (const t of trials) {
    if (t.didUserRespond) {
      seenHit = true;
    } else if (seenHit) {
      if (best === null || t.loudness > best) {
        best = t.loudness;
      }
    }
  }
  return best;
}

/** @see rht-web-app `loudestMissDbForMissJumpReference` */
export function loudestMissDbForMissJumpReference(
  trials: ReadonlyArray<LoudnessTrialRow>,
  currentDb: number,
): number | null {
  let lastHitIdx = -1;
  for (let i = 0; i < trials.length; i++) {
    if (trials[i].didUserRespond) {
      lastHitIdx = i;
    }
  }
  if (lastHitIdx < 0) {
    return null;
  }
  const lastHitDb = trials[lastHitIdx].loudness;
  let seenHit = false;
  let best: number | null = null;
  for (let i = 0; i < lastHitIdx; i++) {
    const t = trials[i];
    if (t.didUserRespond) {
      seenHit = true;
    } else if (seenHit) {
      if (
        t.loudness >= lastHitDb &&
        t.loudness !== currentDb &&
        (best === null || t.loudness > best)
      ) {
        best = t.loudness;
      }
    }
  }
  return best;
}

/**
 * Next stimulus level after one completed trial. `trialHistory` must include the trial that
 * just ended (presentation `currentDb` and whether the user responded / heard).
 */
export function computeNextDbWebAligned(
  currentDb: number,
  heard: boolean,
  hasPressedHeardOnce: boolean,
  trialHistory?: ReadonlyArray<LoudnessTrialRow>,
): number {
  if (heard) {
    if (currentDb >= 10) {
      return Math.max(currentDb - 10, MIN_DB);
    }
    return currentDb;
  }
  if (currentDb <= 80) {
    if (!hasPressedHeardOnce) {
      const step = currentDb === 80 ? 5 : 20;
      return Math.min(currentDb + step, MAX_DB);
    }
    const ordinaryUp = Math.min(currentDb + 5, MAX_DB);
    const X =
      trialHistory && trialHistory.length > 0
        ? loudestMissDbForMissJumpReference(trialHistory, currentDb)
        : null;
    if (X === null) {
      return ordinaryUp;
    }
    const jumpUp = Math.min(X + MISS_JUMP_OVER_LOUDEST_MISS_DB, MAX_DB);
    return jumpUp > ordinaryUp ? jumpUp : ordinaryUp;
  }
  return currentDb;
}

/** @see rht-web-app `sequenceUsesBigMissJump` */
export function sequenceUsesBigMissJump(
  expectedHeardResponses: boolean[],
): boolean {
  let loudness = 45;
  let hasPressedHeardOnce = false;
  const testData: LoudnessTrialRow[] = [];

  for (let i = 0; i < expectedHeardResponses.length; i++) {
    const heard = expectedHeardResponses[i];
    testData.push({ loudness, didUserRespond: heard });

    if (!heard && loudness <= 80 && hasPressedHeardOnce) {
      const ordinaryUp = Math.min(loudness + 5, MAX_DB);
      const X = loudestMissDbForMissJumpReference(testData, loudness);
      if (X !== null) {
        const jumpUp = Math.min(X + MISS_JUMP_OVER_LOUDEST_MISS_DB, MAX_DB);
        if (jumpUp > ordinaryUp) {
          return true;
        }
      }
    }

    if ((!heard && loudness <= 80) || (heard && loudness >= 10)) {
      loudness = computeNextDbWebAligned(
        loudness,
        heard,
        hasPressedHeardOnce,
        testData,
      );
    }
    if (heard) {
      hasPressedHeardOnce = true;
    }
  }
  return false;
}
